import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef, Input } from '@angular/core';
import { RouterLink, RouterModule } from '@angular/router';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { authUser, RolesEnum } from '../../../interfaces/auth/auth.model';
import { AuthService } from '../../../services/auth.service';
import { DirectionService } from '../../../services/direction.service';
import { StrategyService } from '../../../services/strategy.service';
import { SidebarExpanderService } from '../../../services/sidebar-expander.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NAV_LABELS } from '../../../services/page-title.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterModule, TieredMenuModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {

  /** 'sidebar' = left rail, 'header' = horizontal tabs in the top nav */
  @Input() variant: 'sidebar' | 'header' = 'sidebar';

  dir!: "rtl" | "ltr";
  defualtStrategyName = "";
  sidebarToRoleEn = {} as any;
  sidebarToRoleMap = {} as any;
  menuItems = [] as any[];
  sidebarDynamicStatus = false;
  isFullWidth = true;

  get isHeaderVariant(): boolean {
    return this.variant === 'header';
  }

  toggletNavActive(item: any): any {
    if (!item.children?.length) {
      // Plain links: close any open dropdowns in header mode
      if (this.isHeaderVariant) {
        this.closeAllSubMenu();
      }
      return;
    }
    if (!item.active) {
      this.menuItems.forEach((a): any => {
        if (this.menuItems.includes(item)) a.active = false;
        if (!a.children) return false;
        a.children.forEach((b: any) => {
          if (a.children?.includes(item)) {
            b.active = false;
          }
        });
      });
    }
    item.active = !item.active;
  }

  constructor(
    private strategyService: StrategyService, 
    private authService: AuthService,
    private directionService: DirectionService,
    private SidebarExpanderService: SidebarExpanderService,
    private observer: BreakpointObserver,
    private cdr: ChangeDetectorRef
  ) { }
  ngOnInit(): void {
    if (this.isHeaderVariant) {
      this.isFullWidth = true;
      this.sidebarDynamicStatus = false;
    }

    // Listen to sidebar expander service
    this.SidebarExpanderService.isSidebarExpanded$.subscribe((isDynamic) => {
      if (this.isHeaderVariant) {
        this.isFullWidth = true;
        return;
      }
      setTimeout(() => {
        this.sidebarDynamicStatus = isDynamic;
        if (isDynamic) {
          this.isFullWidth = false;
        } else {
          this.isFullWidth = true;
        }
        this.cdr.markForCheck();
      });
    });
    
    this.observer.observe(['(max-width: 800px)']).subscribe((screenSize) => {
      if (this.isHeaderVariant) {
        this.isFullWidth = true;
        return;
      }
      setTimeout(() => {
        if (screenSize.matches) {
          this.isFullWidth = false;
        } else if (!this.sidebarDynamicStatus) {
          this.isFullWidth = true;
        }
        this.cdr.markForCheck();
      });
    });
    this.strategyService.defaultStrategy$.subscribe((strategy) => {

      this.authService.authBufferSub$.subscribe((user) => {
        if (!user) {
          this.menuItems = [];
          return;
        }

        this.directionService.direction$.subscribe((dir) => {
          this.dir = dir;

          const locale = this.dir == 'rtl' ? NAV_LABELS.ar : NAV_LABELS.en;

          this.defualtStrategyName = strategy?.name || locale.defaultStrategy;
          const items = {
            home: {
              path: user?.strategy_exec ? 'strategy-executor-home' : 'strategy-manager-home',
              title: locale.Home,
              icon: 'fa-solid fa-house',
              type: 'link',
              active: false,
            },
            strategies: {
              path: 'strategy',
              icon: 'fa-chess-knight',
              title: locale.strategies,
              active: false,
              type: 'link',
            },
            tasks: {
              path: 'tasks',
              title: locale.tasks,
              icon: 'fa-solid fa-list-check',
              active: false,
              type: 'link'
            },
            overallOperationalPerformance: {
              path: 'dashboard',
              title: locale.overallOperationalPerformance,
              icon: "fa-solid fa-chart-pie",
              type: 'link',
              active: false,
            },
            strategicDashboard: {
              path: 'performance',
              title: locale.strategicDashboard,
              icon: "fa-solid fa-gauge-high",
              type: 'link',
              active: false,
            },
            indicatorsPerformance: {
              path: 'indicators-performance',
              title: locale.indicatorsPerformance,
              icon: 'fa-solid fa-chart-simple',
              type: 'link',
              active: false
            },
            strategyAlignment: {
              path: 'strategy-alignment/0',
              title: locale.strategyAlignment,
              icon: 'fa-solid fa-diagram-project',
              active: false,
              type: 'link'
            },
            organizationHierarchy: {
              path: 'organization-hierarchy',
              title: locale.organizationHierarchy,
              icon: 'fa-solid fa-sitemap',
              active: false,
              type: 'link'
            },
            users: {
              path: 'admin/permission',
              title: locale.users,
              icon: 'fa-solid fa-users',
              type: 'link',
              active: false,
            },
            departmentsItem: {
              path: 'admin/department',
              title: locale.Department,
              icon: 'fa-solid fa-building',
              type: 'link',
              active: false,
            },
            systemManagement: {
              title: locale.systemManagement,
              icon: 'fa-solid fa-sitemap',
              type: 'sub',
              active: false,
              children: [
                // Organization Hierarchy (non-admin route)
                {
                  path: 'organization-hierarchy',
                  title: locale.organizationHierarchy,
                  icon: 'fa-solid fa-sitemap',
                  type: 'link',
                  active: false,
                },
                // Users (admin route)
                {
                  path: 'admin/permission',
                  title: locale.users,
                  icon: 'fa-solid fa-users',
                  type: 'link',
                  active: false,
                },
                // Departments (admin route)
                {
                  path: 'admin/department',
                  title: locale.Department,
                  icon: 'fa-solid fa-building',
                  type: 'link',
                  active: false,
                },
                // Project Edit Permissions (admin route)
                {
                  path: 'admin/project-edit-permissions',
                  title: locale.ProjectEditPermissions,
                  icon: 'fa-solid fa-edit',
                  type: 'link',
                  active: false,
                },
              ]
            },
            fileExplorer: {
              path: 'file-manager',
              title: locale.fileExplorer,
              icon: 'fa-solid fa-folder-tree',
              active: false,
              type: 'link'
            },
            reports: {
              path: 'reports',
              title: locale.reports,
              icon: 'fa-solid fa-file-invoice',
              type: 'link',
              active: false,
            },
            myReports: {
              path: 'my-reports',
              title: locale.myReports,
              icon: 'fa-solid fa-file-chart-line',
              type: 'link',
              active: false,
            },
            departmentApprovals: {
              path: 'department-approvals',
              title: locale.departmentApprovals,
              icon: 'fa-solid fa-clipboard-check',
              type: 'link',
              active: false,
            },
            MilestoneOutputAuditor: {
              path: 'file-auditor',
              title: locale.MilestoneOutputAuditor,
              icon: 'fa-solid fa-file-circle-check',
              type: 'link',
              active: false,
            },
            IndicatorsOutputAuditor: {
              path: 'indicators-file-auditor',
              title: locale.IndicatorsOutputAuditor,
              icon: 'fa-solid fa-file-contract',
              type: 'link',
              active: false,
            }
          };

          this.sidebarToRoleMap = {
            "senior_admin": [
              items.strategicDashboard,
              items.overallOperationalPerformance,
              items.indicatorsPerformance,
              items.strategyAlignment,
              items.systemManagement,
              items.reports
            ],
            "strategy_admin": [
              items.home,
              items.strategies,
              this.getDefaultStrategyItemByUserRole(user),
              items.fileExplorer,
              items.MilestoneOutputAuditor,
              items.IndicatorsOutputAuditor,
              items.strategicDashboard,
              items.overallOperationalPerformance,
              items.indicatorsPerformance,
              items.strategyAlignment,
              items.systemManagement,
              items.reports,
              // {
              //   title: this.dir == 'rtl' ? 'بدون استراتيجية' : 'Without Strategy',
              //   icon: 'fa-solid fa-chart-line',
              //   type: 'sub',
              //   active: false,
              //   children: [
              //     {
              //       path: 'strategy-admin-home',
              //       title: this.dir == 'rtl' ? 'لمدير الاستراتيجية' : 'Strategy Manager',
              //       type: 'link',
              //       active: false,
              //     },
              //     {
              //       path: 'executer-test',
              //       title: this.dir == 'rtl' ? 'لمنفذ الاستراتيجية' : 'Strategy Executor',
              //       type: 'link',
              //       active: false,
              //     },
              //   ]
              // }
            ],
            "department_admin": [
              items.home,
              items.tasks,
              this.getDefaultStrategyItemByUserRole(user),
              items.departmentApprovals,
              items.strategyAlignment,
              items.organizationHierarchy,
              items.myReports,
            ],
            "section_admin": [
              items.home,
              items.tasks,
              this.getDefaultStrategyItemByUserRole(user),
              items.departmentApprovals,
              items.strategyAlignment,
              items.organizationHierarchy,
              items.myReports,
            ],
            "section_employee": [
              items.home,
              items.tasks,
              this.getDefaultStrategyItemByUserRole(user),
              items.strategyAlignment,
              items.organizationHierarchy,
              items.myReports,
            ],
            "admin": [
              {
                path: 'admin/admin-home',
                title: locale.ControlPanel,
                icon: 'fa-solid fa-gears',
                type: 'link',
                active: false,

              },
              {
                path: 'admin/department',
                icon: 'fa-solid fa-building',
                title: locale.Department,
                type: 'link',
                active: false,
              },
              {
                path: 'organization-hierarchy',
                icon: 'fa-solid fa-sitemap',
                title: locale.organizationHierarchy,
                type: 'link',
                active: false,
              },
              {
                path: 'admin/permission',
                title: locale.users,
                icon: 'fa-solid fa-users',
                type: 'link',
                active: false,
              },
              {
                path: 'admin/project-edit-permissions',
                title: locale.ProjectEditPermissions,
                icon: 'fa-solid fa-edit',
                type: 'link',
                active: false,
              },
              {
                path: 'admin/reports',
                title: locale.reports,
                icon: 'fa-solid fa-file-invoice',
                type: 'link',
                active: false,
              },
            ]
          };

          console.log(this.sidebarToRoleMap);
          console.log('role ', user?.role);

          if (user?.senior_admin) {
            this.menuItems = this.sidebarToRoleMap.senior_admin
          }
          else if (user?.strategy_admin) {
            this.menuItems = this.sidebarToRoleMap.strategy_admin
          }
          else if (user?.department_admin || user?.section_admin || user?.section_employee || user?.role === RolesEnum.department_secretary) {
            this.menuItems = this.sidebarToRoleMap[user.role] || this.sidebarToRoleMap.section_employee
          }
          else if (user?.admin) {
            this.menuItems = this.sidebarToRoleMap.admin
          }
        })
      })
    })
  };

  getDefaultStrategyItemByUserRole(user: authUser) {
    const locale = this.dir == 'rtl' ? NAV_LABELS.ar : NAV_LABELS.en;
    return {
      title: this.defualtStrategyName,
      icon: 'fa-solid fa-chart-line',
      type: 'sub',
      active: false,
      children:
        [
          {
            path: 'strategies/goals',
            title: locale.viewGoals,
            type: 'link',
            active: false,
          },
          {
            path: 'strategies/indicators',
            title: user.strategy_admin ? locale.viewIndicators : locale.viewMyIndicators,
            type: 'link',
            active: false,
          },
          {
            path: 'strategies/projects',
            title: user.strategy_admin ? locale.viewInitiatives :
              user.department_admin ? locale.viewDepartmentInitiatives :
                user.section_admin ? locale.viewSectionInitiatives : locale.viewMyInitiatives,
            type: 'link',
            active: false,
          }
        ]
    };
  };
  toggletSideBar(status: boolean) {
    this.SidebarExpanderService.setDynamic(status);
    setTimeout(() => {
      this.sidebarDynamicStatus = status;
      if (this.sidebarDynamicStatus) {
        this.sidebarHoverpOut();
      }
      this.cdr.markForCheck();
    });
  }
  sidebarIsHoverd = false;
  sidebarHoverIn() {
    if (this.isHeaderVariant) return;
    this.sidebarIsHoverd = true;
    if (this.sidebarDynamicStatus) {
      setTimeout(() => {
        this.isFullWidth = true;
        this.cdr.markForCheck();
      });
    }
  }
  
  sidebarHoverpOut() {
    if (this.isHeaderVariant) return;
    this.sidebarIsHoverd = false;
    if (this.sidebarDynamicStatus) {
      setTimeout(() => {
        this.isFullWidth = false;
        this.closeAllSubMenu();
        this.cdr.markForCheck();
      });
    }
  }

  closeAllSubMenu() {
    this.menuItems.forEach((item): any => {
      item.active = false;
    });
  }
}
